import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useCrmTenant } from '../crm/useCrmTenant';
import { paymentApi } from '../../services';
import { computePlanBreakdowns } from '../../utils/paymentCalc';
import { extractPlans, mergeImportedPlans } from '../../utils/planImport';
import {
  GatewayPlan,
  ONE_TIME_CADENCES,
  PLAN_CADENCES,
  formatOptionPriceSuffix,
  isOneTimePlan,
  formatPlanMoney,
  formatPlanPrice,
  PAYMENT_PROVIDERS,
  PaymentPlanOption,
  PaymentProvider,
  RECURRING_PROVIDERS,
} from '../../services/api/paymentApi';
import { ADMIN_COLORS } from '../../theme';

interface PaymentProviderEditorProps {
  providers?: PaymentProvider[];
  paymentMode?: 'one_time' | 'plan';
  planOptions?: PaymentPlanOption[];
  gstRate?: number | string;
  planApi?: { url?: string; path?: string };
  onChange: (patch: Record<string, unknown>) => void;
}

const providerLabel = (value: string) =>
  PAYMENT_PROVIDERS.find((p) => p.value === value)?.label || value;

const newPlanId = () => `plan_${Math.random().toString(36).slice(2, 10)}`;


const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: 8,
  marginBottom: 10,
};

const Labeled: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'block', margin: 0, fontSize: 11, fontWeight: 600, color: ADMIN_COLORS.textMuted }}>
    {label}
    {children}
  </label>
);

/** Price, cadence, discount and GST for one plan, with a live preview of what the visitor pays. */
const PlanPricingFields: React.FC<{
  plan: PaymentPlanOption;
  gstRate?: number | string;
  onChange: (patch: Partial<PaymentPlanOption>) => void;
}> = ({ plan, gstRate, onChange }) => {
  const currency = 'INR';
  const rate = gstRate === undefined || gstRate === '' ? 18 : Number(gstRate);
  const priced = plan.price !== undefined && plan.price !== '';
  const cadence = plan.cadence || 'Monthly';
  const oneTime = isOneTimePlan(plan);
  const fixedMultiple = cadence === 'Quarterly' || cadence === 'Half-Yearly' || cadence === 'Lifetime';
  const discountMode = plan.discountMode || 'none';
  const preview = priced ? computePlanBreakdowns(plan, Number.isFinite(rate) ? rate : 18) : null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={grid}>
        <Labeled label={`Price (${currency})`}>
          <input
            type="number"
            min={0}
            className="form-control form-control-sm"
            value={plan.price ?? ''}
            onChange={(e) => onChange({ price: e.target.value })}
          />
        </Labeled>
        <Labeled label="Payment">
          <select
            className="form-control form-control-sm"
            value={oneTime ? 'one_time' : 'recurring'}
            onChange={(e) => {
              const next = e.target.value as 'recurring' | 'one_time';
              onChange({
                payment: next,
                // Lifetime only makes sense for a one-time plan.
                ...(next === 'recurring' && plan.cadence === 'Lifetime' ? { cadence: 'Monthly' } : {}),
              });
            }}
          >
            <option value="recurring">Recurring</option>
            <option value="one_time">One-time</option>
          </select>
        </Labeled>
        <Labeled label={oneTime ? 'Valid for' : 'Billing cycle'}>
          <select
            className="form-control form-control-sm"
            value={cadence}
            onChange={(e) => onChange({ cadence: e.target.value as PaymentPlanOption['cadence'] })}
          >
            {(oneTime ? ONE_TIME_CADENCES : PLAN_CADENCES).map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </Labeled>
        <Labeled label={oneTime ? 'For N periods' : 'Every N cycles'}>
          <input
            type="number"
            min={1}
            disabled={fixedMultiple}
            className="form-control form-control-sm"
            value={plan.intervalCount ?? ''}
            placeholder="1"
            onChange={(e) => onChange({ intervalCount: e.target.value })}
          />
        </Labeled>
        {!oneTime && (
          <Labeled label="Max cycles">
            <input
              type="number"
              min={1}
              className="form-control form-control-sm"
              value={plan.maxCycles ?? ''}
              placeholder="12"
              onChange={(e) => onChange({ maxCycles: e.target.value })}
            />
          </Labeled>
        )}
      </div>
      <div style={grid}>
        <Labeled label="Discount">
          <select
            className="form-control form-control-sm"
            value={discountMode}
            onChange={(e) => onChange({ discountMode: e.target.value as PaymentPlanOption['discountMode'] })}
          >
            <option value="none">None</option>
            <option value="percentage">Percentage</option>
            <option value="flat">Flat</option>
          </select>
        </Labeled>
        {discountMode !== 'none' && (
          <>
            <Labeled label={discountMode === 'flat' ? `Amount off (${currency})` : 'Percent off'}>
              <input
                type="number"
                min={0}
                className="form-control form-control-sm"
                value={plan.discountValue ?? ''}
                onChange={(e) => onChange({ discountValue: e.target.value })}
              />
            </Labeled>
            {!oneTime && <Labeled label="Applies to">
              <select
                className="form-control form-control-sm"
                value={plan.discountType || 'all_time'}
                onChange={(e) => onChange({ discountType: e.target.value as PaymentPlanOption['discountType'] })}
              >
                <option value="all_time">Every payment</option>
                <option value="first_time">First payment only</option>
              </select>
            </Labeled>}
          </>
        )}
        <Labeled label="GST">
          <select
            className="form-control form-control-sm"
            value={plan.gstMode || 'none'}
            onChange={(e) => onChange({ gstMode: e.target.value as PaymentPlanOption['gstMode'] })}
          >
            <option value="none">None</option>
            <option value="included">Included in price</option>
            <option value="excluded">Added on top</option>
          </select>
        </Labeled>
      </div>
      <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
        <label style={{ margin: 0, fontSize: 12, }}>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={Boolean(plan.isPopular)}
              onChange={(e) => onChange({ isPopular: e.target.checked })}
            />
            <span>Mark as most popular</span>
          </div>
        </label>
        {preview && (
          <span style={{ fontSize: 12, color: ADMIN_COLORS.accent, fontWeight: 600 }}>
            {oneTime ? 'Pays' : 'First payment'} {formatPlanMoney(preview.first.total, currency)}
            {!oneTime && preview.recurring.total !== preview.first.total
              ? `, then ${formatPlanMoney(preview.recurring.total, currency)}`
              : ''}{' '}
            {formatOptionPriceSuffix(plan)}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Fills plans from an API, e.g. finuro's public subscriptions endpoint. Plans stay
 * editable here and are created on the gateways when the form is saved, so the
 * form remains the source of truth.
 */
const PlanApiImport: React.FC<{
  planApi?: { url?: string; path?: string };
  planOptions: PaymentPlanOption[];
  onChange: (patch: Record<string, unknown>) => void;
  providers: PaymentProvider[];
}> = ({ planApi, planOptions, providers, onChange }) => {
  const crmTenantId = useCrmTenant();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);
  const url = planApi?.url || '';
  const path = planApi?.path || '';
  const setApi = (patch: { url?: string; path?: string }) => onChange({ planApi: { url, path, ...patch } });

  const fetchPlans = async () => {
    if (!/^https?:\/\//i.test(url.trim())) {
      setMessage({ text: 'Enter a full http(s) URL.', error: true });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const response = await axios.get(url.trim(), { timeout: 20000 });
      const { plans, skipped } = extractPlans(response.data, path);
      if (!plans.length) {
        setMessage({
          text: `No recurring plans found${skipped ? ` (${skipped} skipped: disabled, no price or unknown cycle)` : ''}. Check the list path.`,
          error: true,
        });
        return;
      }
      // Reuse gateway plans the API names when they exist on this tenant's gateways.
      const onGateway: Partial<Record<PaymentProvider, Set<string>>> = {};
      await Promise.all(
        providers.map(async (provider) => {
          try {
            const list = crmTenantId ? await paymentApi.getPlans(crmTenantId, provider) : [];
            onGateway[provider] = new Set(list.map((gp) => gp.id));
          } catch {
            onGateway[provider] = new Set();
          }
        }),
      );
      let linked = 0;
      plans.forEach((plan) => {
        const found: Partial<Record<PaymentProvider, string>> = {};
        providers.forEach((provider) => {
          const id = plan.adoptGatewayPlans?.[provider];
          if (id && onGateway[provider]?.has(id)) found[provider] = id;
        });
        plan.adoptGatewayPlans = found;
        plan.gatewayPlans = { ...found };
        if (Object.keys(found).length) linked += 1;
      });
      const merged = mergeImportedPlans(planOptions, plans, newPlanId);
      onChange({ planOptions: merged.plans });
      setMessage({
        text: `${merged.added} added, ${merged.updated} updated${skipped ? `, ${skipped} skipped` : ''}${linked ? `; ${linked} matched existing gateway plans on this tenant` : ''}. Save the form to ${linked ? 'verify them and create any missing' : 'create them on'} the gateways.`,
      });
    } catch (err: any) {
      setMessage({ text: err?.message || 'Could not fetch plans from that URL.', error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-group">
      <label className="control-label" htmlFor="planApiUrl">Fill plans from an API</label>
      <input
        id="planApiUrl"
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder="https://…/api/subscription/public/your-subscription"
        value={url}
        onChange={(e) => setApi({ url: e.target.value })}
      />
      <input
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder="List path (optional), e.g. plans or data.plans"
        aria-label="List path in the response"
        value={path}
        onChange={(e) => setApi({ path: e.target.value })}
      />
      <button type="button" className="rfb-add-btn" disabled={busy || !url.trim()} onClick={fetchPlans}>
        {busy ? 'Fetching…' : 'Fetch & fill plans'}
      </button>
      {message && (
        <small className={`form-text ${message.error ? 'text-danger' : 'text-success'}`}>{message.text}</small>
      )}
      <small className="form-text text-muted">
        Works with a finuro subscription (price, cycle, discount, GST and popular flag are mapped), a subscription list, or any plain plan list. Plans fetched earlier are updated, not duplicated.
      </small>
    </div>
  );
};

/** Provider, one-time/plan mode and plan setup for the Payment block. */
const PaymentProviderEditor: React.FC<PaymentProviderEditorProps> = ({
  providers,
  paymentMode = 'one_time',
  planOptions = [],
  gstRate,
  planApi,
  onChange,
}) => {
  const crmTenantId = useCrmTenant();
  const selected: PaymentProvider[] = providers?.length ? providers : ['cashfree'];
  const isPlanMode = paymentMode === 'plan';
  const planProviders = selected.filter((p) => RECURRING_PROVIDERS.includes(p));
  const [available, setAvailable] = useState<
    Partial<Record<PaymentProvider, GatewayPlan[]>>
  >({});
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isPlanMode || !crmTenantId) return;
    let cancelled = false;
    setLoadError('');
    planProviders.forEach((provider) => {
      paymentApi
        .getPlans(crmTenantId, provider)
        .then((list) => !cancelled && setAvailable((cur) => ({ ...cur, [provider]: list })))
        .catch(() => !cancelled && setLoadError(`Could not load ${providerLabel(provider)} plans.`));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlanMode, crmTenantId, planProviders.join(',')]);

  const toggleProvider = (value: PaymentProvider) => {
    const next = selected.includes(value)
      ? selected.filter((p) => p !== value)
      : [...selected, value];
    if (!next.length) return;
    onChange({ providers: next });
  };

  const updatePlan = (id: string, patch: Partial<PaymentPlanOption>) =>
    onChange({
      planOptions: planOptions.map((plan) => (plan.id === id ? { ...plan, ...patch } : plan)),
    });

  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const movePlan = (fromId: string, toId: string) => {
    const from = planOptions.findIndex((p) => p.id === fromId);
    const to = planOptions.findIndex((p) => p.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...planOptions];
    next.splice(to, 0, next.splice(from, 1)[0]);
    onChange({ planOptions: next });
  };

  const shiftPlan = (index: number, delta: number) => {
    const target = planOptions[index + delta];
    if (target) movePlan(planOptions[index].id, target.id);
  };

  const setPlanFor = (plan: PaymentPlanOption, provider: PaymentProvider, gatewayPlanId: string) => {
    const gatewayPlans = { ...plan.gatewayPlans };
    if (gatewayPlanId) gatewayPlans[provider] = gatewayPlanId;
    else delete gatewayPlans[provider];
    // Default the card label to the gateway's plan name until the author types one.
    const picked = (available[provider] || []).find((gp) => gp.id === gatewayPlanId);
    updatePlan(plan.id, {
      gatewayPlans,
      ...(!plan.label.trim() && picked?.name ? { label: picked.name } : {}),
    });
  };

  return (
    <>
      <div className="form-group">
        <label className="control-label">Payment Providers</label>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          {PAYMENT_PROVIDERS.map(({ value, label }) => {
            const on = selected.includes(value);
            return (
              <button
                key={value}
                type="button"
                aria-pressed={on}
                onClick={() => toggleProvider(value)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  border: `1px solid ${on ? ADMIN_COLORS.accent : ADMIN_COLORS.borderStrong}`,
                  background: on ? ADMIN_COLORS.accentSoft : ADMIN_COLORS.surface,
                  color: on ? ADMIN_COLORS.accent : ADMIN_COLORS.textSecondary,
                }}
              >
                {on ? '✓ ' : ''}
                {label}
              </button>
            );
          })}
        </div>
        <small className="form-text text-muted">
          With more than one, the visitor chooses how to pay. The first selected is the default.
        </small>
      </div>

      <div className="form-group">
        <label className="control-label" htmlFor="paymentMode">Payment Type</label>
        <select
          id="paymentMode"
          className="form-control w-50"
          value={paymentMode}
          onChange={(e) => {
            const mode = e.target.value as 'one_time' | 'plan';
            onChange({ paymentMode: mode });
          }}
        >
          <option value="one_time">One-time payment</option>
          <option value="plan">Recurring (subscription plans)</option>
        </select>
        {isPlanMode && (
          <small className="form-text text-muted">
            Amount, discount and GST come from the plan. HDFC can only be used with one-time plans.
          </small>
        )}
      </div>

      {isPlanMode && (
        <>
          <PlanApiImport
            providers={planProviders}
            planApi={planApi}
            planOptions={planOptions}
            onChange={onChange}
          />
          <div className="form-group">
            <label className="control-label">Plans</label>
            {loadError && <small className="form-text text-danger">{loadError}</small>}
            {planOptions.map((plan, index) => (
              <div
                key={plan.id}
                onDragOver={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  setOverId(plan.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragId) movePlan(dragId, plan.id);
                  setDragId(null);
                  setOverId(null);
                }}
                style={{
                  opacity: dragId === plan.id ? 0.5 : 1,
                  outline: dragId && overId === plan.id && dragId !== plan.id ? `2px dashed ${ADMIN_COLORS.accent}` : 'none',
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  borderRadius: 12,
                  marginBottom: 12,
                  background: ADMIN_COLORS.surface,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ background: ADMIN_COLORS.headBg, padding: '8px 12px', gap: 10 }}
                >
                  <span
                    draggable
                    title="Drag to reorder"
                    aria-label={`Drag plan ${index + 1} to reorder`}
                    onDragStart={(e) => {
                      setDragId(plan.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', plan.id);
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverId(null);
                    }}
                    style={{ cursor: 'grab', color: ADMIN_COLORS.textFaint, fontSize: 16, userSelect: 'none' }}
                  >
                    ⋮⋮
                  </span>
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: ADMIN_COLORS.accent,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Plan label (e.g. Monthly)"
                    value={plan.label}
                    onChange={(e) => updatePlan(plan.id, { label: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Move plan ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => shiftPlan(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Move plan ${index + 1} down`}
                    disabled={index === planOptions.length - 1}
                    onClick={() => shiftPlan(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remove plan ${index + 1}`}
                    onClick={() => onChange({ planOptions: planOptions.filter((p) => p.id !== plan.id) })}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ padding: 12 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm mb-3"
                    placeholder="Short description (optional)"
                    value={plan.description || ''}
                    onChange={(e) => updatePlan(plan.id, { description: e.target.value })}
                  />
                  <PlanPricingFields
                    plan={plan}
                    gstRate={gstRate}
                    onChange={(patch) => updatePlan(plan.id, patch)}
                  />
                  {!isOneTimePlan(plan) && planProviders.map((provider) => {
                    const chosenId = plan.gatewayPlans?.[provider] || '';
                    const managed = plan.price !== undefined && plan.price !== '';
                    const info = (available[provider] || []).find((gp) => gp.id === chosenId);
                    return (
                      <div
                        key={provider}
                        className="d-flex align-items-center mb-2"
                        style={{ gap: 10 }}
                      >
                        <span
                          style={{
                            width: 84,
                            flexShrink: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            color: ADMIN_COLORS.textSecondary,
                            background: ADMIN_COLORS.segmentBg,
                            borderRadius: 999,
                            textAlign: 'center',
                            padding: '3px 0',
                          }}
                        >
                          {providerLabel(provider)}
                        </span>
                        {managed ? (
                          <span style={{ flex: 1, fontSize: 12, color: chosenId ? ADMIN_COLORS.success : ADMIN_COLORS.warning }}>
                            {chosenId
                              ? `Gateway plan ${chosenId}. Updated automatically when you save.`
                              : 'Gateway plan is created when you save the form.'}
                          </span>
                        ) : (
                        <select
                          className="form-control form-control-sm"
                          value={chosenId}
                          onChange={(e) => setPlanFor(plan, provider, e.target.value)}
                        >
                          <option value="">Not available on this provider</option>
                          {(available[provider] || []).map((gp) => (
                            <option key={gp.id} value={gp.id}>
                              {gp.name}
                            </option>
                          ))}
                          {chosenId && !info && <option value={chosenId}>{chosenId}</option>}
                        </select>
                        )}
                        {info && formatPlanPrice(info) && (
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              fontSize: 12,
                              fontWeight: 700,
                              color: ADMIN_COLORS.accent,
                              background: ADMIN_COLORS.accentSoft,
                              borderRadius: 999,
                              padding: '3px 10px',
                            }}
                          >
                            {formatPlanPrice(info)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              type="button"
              className="rfb-add-btn"
              onClick={() =>
                onChange({
                  planOptions: [
                    ...planOptions,
                    {
                      id: newPlanId(),
                      label: '',
                      price: '',
                      payment: 'recurring',
                      cadence: 'Monthly',
                      discountMode: 'none',
                      discountType: 'all_time',
                      gstMode: 'none',
                      gatewayPlans: {},
                    },
                  ],
                })
              }
            >
              <span aria-hidden>+</span> Add plan
            </button>
            <small className="form-text text-muted">
              Give a plan a price and the gateway plan is created from it when you save the form . Leave the price empty to pick an existing gateway plan instead. Drag the handle or use the arrows to set the order visitors see. Visitors see each plan with its price and interval. They pick a plan first, then a provider that offers it.
            </small>
          </div>
        </>
      )}
    </>
  );
};

export default PaymentProviderEditor;
